"use client";

import {useContextStore} from "@/src/stores/useContextStore";
import {useMemo} from "react";
import {has} from "lodash";
import {ContextInstanceVariableType} from "@/src/shared/schemas";
import {FormProvider, useForm} from "react-hook-form";
import {Element} from "@craftjs/core";
import { FlexBox } from "@/src/components/FlexBox";
import { KeyValue } from "@/src/components/OrderData";

const touristData = [
    {
        title: "Період дії",
        items: [
            // Period section items
            {
                name: "Дата початку дії договору",
                context: ["startDate"],
                editable: true,
            },
            {
                name: "Дата завершення договору",
                context: ["endDate"],
                editable: false,
            },
            {
                name: "Період страхування",
                context: ["insurancePeriod"],
                editable: false,
            },
        ]
    },
    {
        title: "Умови страхування",
        items: [
            // Insurance conditions items
            {
                name: "Територія дії",
                context: ["territory"],
                editable: false,
            },
            {
                name: "Мета подорожі",
                context: ["tripPurpose"],
                editable: false,
            },
            {
                name: "Кількість туристів",
                context: ["touristsCount"],
                editable: false,
            },
            {
                name: "Програма",
                context: ["touristProgram"],
                editable: false,
            },
            {
                name: "Вартість страхування",
                context: ["cost"],
                editable: false,
            },
        ]
    },
    {
        title: "Дані страхувальника (покупця)",
        items: [
            // Policyholder items
            {
                name: "Прізвище, ім'я (латиницею)",
                context: ["firstName", "lastName"],
                editable: true,
            },
            {
                name: "Дата народження",
                context: ["birthDate"],
                editable: true,
            },
            {
                name: "Ідентифікаційний номер",
                context: ["identificationNumber"],
                editable: true,
            },
            {
                name: "Номер телефону",
                context: ["phoneNumber"],
                editable: true,
            },
            {
                name: "Електронна пошта",
                context: ["email"],
                editable: true,
            },

            {
                name: "Документ",
                context: ["documentType", "documentNumber", "documentSeries"],
                editable: true,
            }
        ]
    },
    {
        title: "Дані застрахованого (Турист 1)",
        items: [
            // Tourist items
            {
                name: "Прізвище, ім'я (латиницею)",
                context: ["firstName", "lastName"],
                editable: true,
            },
            {
                name: "Дата народження",
                context: ["birthDate"],
                editable: true,
            },
            {
                name: "Ідентифікаційний номер",
                context: ["identificationNumber"],
                editable: true,
            },
            {
                name: "Закордонний паспорт",
                context: ["passportSeries", "passportNumber",],
                editable: true,
            },
        ]
    }
]

const autoCivilData = [
    {
        title: "Період дії",
        items: [
            // Period section items
            {
                name: "Дата початку дії договору",
                context: ["startDate"],
                editable: true,
            },
            {
                name: "Дата завершення договору",
                context: ["endDate"],
                editable: false,
            },
            {
                name: "Період страхування",
                context: ["insurancePeriod"],
                editable: false,
            },
        ]
    },
    {
        title: "Умови страхування",
        items: [
            // Insurance conditions items
            {
                name: "Франшиза",
                context: ["franchise"],
                editable: false,
            },
            {
                name: "Автоцивілка",
                context: ["autoCivil"],
                editable: false,
            },
            {
                name: "Додаткове покриття на 100 000 грн",
                context: ["additionalCoverage"],
                editable: false,
            },
            {
                name: "Медичне страхування при ДТП",
                context: ["medicalInsurance"],
                editable: false,
            },
        ]
    },
    {
        title: "Транспортний засіб",
        items: [
            // Vehicle items
            {
                name: "Марка і модель",
                context: ["brand", "model"],
                editable: true,
            },
            {
                name: "Номерний знак",
                context: ["plateNumber"],
                editable: true,
            },
            {
                name: "VIN номер кузова/шасі",
                context: ["vinNumber"],
                editable: true,
            },
            {
                name: "Рік випуску",
                context: ["yearCreated"],
                editable: true,
            },
        ]
    },
    {
        title: "Страхувальник",
        items: [
            // Policyholder items
            {
                name: "ПІБ страхувальника",
                context: ["firstName", "lastName", "patronymic"],
                editable: true,
            },
            {
                name: "Номер телефону",
                context: ["phoneNumber"],
                editable: true,
            },
            {
                name: "Електронна пошта",
                context: ["email"],
                editable: true,
            },
            {
                name: "Дата народження",
                context: ["birthDate"],
                editable: true,
            },
            {
                name: "Ідентифікаційний номер",
                context: ["identificationNumber"],
                editable: true,
            },
            {
                name: "Документ",
                context: ["documentType", "documentNumber", "documentSeries"],
                editable: true,
            }
        ]
    }
]

const greenCardData = [
    {
        title: "Період дії",
        items: [
            // Period section items
            {
                name: "Дата початку дії договору",
                context: ["startDate"],
                editable: true,
            },
            {
                name: "Дата завершення договору",
                context: ["endDate"],
                editable: false,
            },
            {
                name: "Період страхування",
                context: ["insurancePeriod"],
                editable: false,
            },
        ]
    },
    {
        title: "Умови страхування",
        items: [
            // Insurance conditions items
            {
                name: "Територія дії",
                context: ["territory"],
                editable: false,
            },
            {
                name: "Вартість",
                context: ["cost"],
                editable: false,
            },
        ]
    },
    {
        title: "Транспортний засіб",
        items: [
            // Vehicle items
            {
                name: "Номерний знак",
                context: ["plateNumber"],
                editable: true,
            },
            {
                name: "VIN номер кузова/шасі",
                context: ["vinNumber"],
                editable: true,
            },
            {
                name: "Марка",
                context: ["brand"],
                editable: true,
            },
        ]
    },
    {
        title: "Страхувальник",
        items: [
            // Policyholder items
            {
                name: "Прізвище, ім'я (латиницею)",
                context: ["firstName", "lastName"],
                editable: true,
            },
            {
                name: "Номер телефону",
                context: ["phoneNumber"],
                editable: true,
            },
            {
                name: "Електронна пошта",
                context: ["email"],
                editable: true,
            },
            {
                name: "Дата народження",
                context: ["birthDate"],
                editable: true,
            },
            {
                name: "Ідентифікаційний номер",
                context: ["identificationNumber"],
                editable: true,
            },
            {
                name: "Документ",
                context: ["documentType", "documentNumber", "documentSeries"],
                editable: true,
            }
        ]
    }
]
const autoProtectionData = [
    {
        title: "Період дії",
        items: [
            // Period section items
            {
                name: "Дата початку дії договору",
                context: ["startDate"],
                editable: true,
            },
            {
                name: "Дата завершення договору",
                context: ["endDate"],
                editable: false,
            },
            {
                name: "Період страхування",
                context: ["insurancePeriod"],
                editable: false,
            },
        ]
    },
    {
        title: "Умови страхування",
        items: [
            // Insurance conditions items
            // todo
            {
                name: "Франшиза",
                context: ["franchise"],
                editable: false,
            },
            {
                name: "Вартість",
                context: ["cost"],
                editable: false,
            },
            // todo
            {
                name: "Сума покриття",
                context: ["coverageAmount"],
                editable: false,
            },
        ]
    },
    {
        title: "Транспортний засіб",
        items: [
            // Vehicle items
            {
                name: "Номерний знак",
                context: ["plateNumber"],
                editable: true,
            },
            {
                name: "VIN номер кузова/шасі",
                context: ["vinNumber"],
                editable: true,
            },
            // todo
            {
                name: "Марка і модель",
                context: ["brand", "model"],
                editable: true,
            },
        ]
    },
    {
        title: "Страхувальник",
        items: [
            // Policyholder items
            // todo
            {
                name: "ПІБ страхувальника",
                context: ["firstName", "lastName", "patronymic"],
                editable: true,
            },
            {
                name: "Номер телефону",
                context: ["phoneNumber"],
                editable: true,
            },
            {
                name: "Електронна пошта",
                context: ["email"],
                editable: true,
            },
            {
                name: "Дата народження",
                context: ["birthDate"],
                editable: true,
            },
            {
                name: "Ідентифікаційний номер",
                context: ["identificationNumber"],
                editable: true,
            },
            {
                name: "Документ",
                context: ["documentType", "documentNumber", "documentSeries"],
                editable: true,
            }
        ]
    }
]

const dataMap: Record<string, {
    title: string,
    items: {
        name: string,
        context: string[],
        editable: boolean
    }[]
}[]> = {
    "tourist": touristData,
    "autoCivil": autoCivilData,
    "greenCard": greenCardData,
    "autoProtection": autoProtectionData,
}

export const OrderPreview = (props: any) => {
    const {product = "autoCivil"} = props;
   return (
        <Element key={"order-preview" + product} is={FlexBox} canvas id={"order-preview" + product} style={{flexDirection: "column", gap: 16}}>
            {
                dataMap[product].map((item, index) => (
                    <Element key={item.title} canvas is={KeyValue} {...item}/>
                ))
            }
        </Element>
    )
}
